import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Collapse,
  Tooltip,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BuildIcon from '@mui/icons-material/Build';
import SendIcon from '@mui/icons-material/Send';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { interventionNotesApi, meterReadingsApi } from '../../services/api';

/**
 * ReadingSheetPreview - Dialog fullscreen per anteprima tabulato letture
 * Consente di visualizzare i contatori organizzati per impianto,
 * aggiungere note intervento a contatori e impianti,
 * e poi generare il PDF.
 */
const ReadingSheetPreview = ({ open, onClose, data, printParams, onDataRefresh }) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Stato per aggiunta note inline
  const [addingNoteMeter, setAddingNoteMeter] = useState(null); // meter_id attivo
  const [addingNotePlant, setAddingNotePlant] = useState(null); // plant_id attivo
  const [newNoteText, setNewNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const handlePrint = useCallback(async () => {
    try {
      setIsPrinting(true);
      const response = await meterReadingsApi.getReadingSheet(printParams);
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      let filename = 'tabulato_letture';
      if (printParams.plant_id) {
        filename += `_impianto_${printParams.plant_id}`;
      } else if (printParams.provincia) {
        filename += `_${printParams.provincia}`;
      }
      filename += `_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSnackbar({ open: true, message: 'Tabulato PDF generato con successo', severity: 'success' });
    } catch (err) {
      console.error('Errore generazione PDF:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Errore nella generazione del PDF',
        severity: 'error'
      });
    } finally {
      setIsPrinting(false);
    }
  }, [printParams]);

  const handleAddMeterNote = useCallback(async (meterId, plantId) => {
    if (!newNoteText.trim()) return;
    try {
      setSavingNote(true);
      await interventionNotesApi.create({
        meter_id: meterId,
        plant_id: plantId,
        tipo: 'contatore',
        nota: newNoteText.trim()
      });
      setNewNoteText('');
      setAddingNoteMeter(null);
      setSnackbar({ open: true, message: 'Nota intervento aggiunta al contatore', severity: 'success' });
      if (onDataRefresh) onDataRefresh();
    } catch (err) {
      console.error('Errore aggiunta nota:', err);
      setSnackbar({ open: true, message: 'Errore nel salvataggio della nota', severity: 'error' });
    } finally {
      setSavingNote(false);
    }
  }, [newNoteText, onDataRefresh]);

  const handleAddPlantNote = useCallback(async (plantId) => {
    if (!newNoteText.trim()) return;
    try {
      setSavingNote(true);
      await interventionNotesApi.create({
        plant_id: plantId,
        tipo: 'impianto',
        nota: newNoteText.trim()
      });
      setNewNoteText('');
      setAddingNotePlant(null);
      setSnackbar({ open: true, message: 'Nota intervento aggiunta all\'impianto', severity: 'success' });
      if (onDataRefresh) onDataRefresh();
    } catch (err) {
      console.error('Errore aggiunta nota impianto:', err);
      setSnackbar({ open: true, message: 'Errore nel salvataggio della nota', severity: 'error' });
    } finally {
      setSavingNote(false);
    }
  }, [newNoteText, onDataRefresh]);

  const handleDeleteNote = useCallback(async (noteId) => {
    try {
      await interventionNotesApi.delete(noteId);
      setSnackbar({ open: true, message: 'Nota eliminata', severity: 'success' });
      if (onDataRefresh) onDataRefresh();
    } catch (err) {
      setSnackbar({ open: true, message: 'Errore eliminazione nota', severity: 'error' });
    }
  }, [onDataRefresh]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (!value || value === 0) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
  };

  if (!data) return null;

  const { plants, totalMeters, totalPlants, showInsoluto } = data;

  return (
    <>
      <Dialog open={open} onClose={onClose} fullScreen>
        <AppBar sx={{ position: 'relative', bgcolor: '#1976d2' }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={onClose} aria-label="chiudi">
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Anteprima Tabulato Letture
            </Typography>
            <Chip 
              label={`${totalPlants} impianti — ${totalMeters} contatori`} 
              sx={{ mr: 2, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
            <Button 
              color="inherit" 
              variant="outlined"
              startIcon={isPrinting ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
              onClick={handlePrint}
              disabled={isPrinting}
              sx={{ borderColor: 'rgba(255,255,255,0.5)' }}
            >
              {isPrinting ? 'Generazione...' : 'Genera PDF'}
            </Button>
          </Toolbar>
        </AppBar>

        <DialogContent sx={{ bgcolor: '#f5f5f5', p: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Puoi aggiungere note di intervento ai contatori e agli impianti prima di stampare il tabulato.
            Le note appariranno evidenziate in arancione nel PDF stampato.
          </Alert>

          {plants.map((plant, plantIndex) => (
            <Paper key={plant.plant_id} sx={{ mb: 3, overflow: 'hidden' }} elevation={2}>
              {/* Header impianto */}
              <Box sx={{
                bgcolor: '#1976d2',
                color: 'white',
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Box>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOnIcon /> {plant.plant_code}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {plant.indirizzo}{plant.citta ? `, ${plant.citta}` : ''} {plant.provincia ? `(${plant.provincia})` : ''}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2">
                    Impianto {plantIndex + 1}/{totalPlants}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {plant.meters.length} contatori
                  </Typography>
                </Box>
              </Box>

              {/* Tabella contatori */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#333' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 600, width: 60 }} align="center">Progr.</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, width: 100 }}>N. Contatore</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Cliente</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Indirizzo</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, width: 90 }} align="center">Data</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, width: 100 }} align="right">Ult. Lettura</TableCell>
                      {showInsoluto && (
                        <TableCell sx={{ color: 'white', fontWeight: 600, width: 100 }} align="right">Insoluto</TableCell>
                      )}
                      <TableCell sx={{ color: 'white', fontWeight: 600, width: 50 }} align="center">Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plant.meters.map((meter, meterIndex) => (
                      <React.Fragment key={meter.id}>
                        {/* Riga contatore */}
                        <TableRow 
                          sx={{ 
                            bgcolor: meter.meterNotes?.length > 0 
                              ? '#fff3e0' 
                              : (meter.insoluto > 0.1 && showInsoluto ? '#ffebee' : (meterIndex % 2 === 0 ? '#fff' : '#f5f5f5')),
                            '&:hover': { bgcolor: meter.meterNotes?.length > 0 ? '#ffe0b2' : '#e3f2fd' },
                            ...(meter.meterNotes?.length > 0 && { borderLeft: '4px solid #ff9800' }),
                          }}
                        >
                          <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                            {meter.sotto_zona}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '14px', fontFamily: 'Consolas, monospace' }}>
                            {meter.meter_number}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>{meter.customer_name}</TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>{meter.localita}</TableCell>
                          <TableCell align="center">{formatDate(meter.data_ultima_lettura)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                            {meter.ultima_lettura != null ? meter.ultima_lettura : '-'}
                          </TableCell>
                          {showInsoluto && (
                            <TableCell align="right" sx={{ color: meter.insoluto > 0.1 ? '#c62828' : 'inherit', fontWeight: meter.insoluto > 0.1 ? 'bold' : 'normal' }}>
                              {meter.insoluto > 0.1 ? formatCurrency(meter.insoluto) : '-'}
                            </TableCell>
                          )}
                          <TableCell align="center">
                            <Tooltip title="Aggiungi nota intervento">
                              <IconButton 
                                size="small" 
                                color={meter.meterNotes?.length > 0 ? 'warning' : 'default'}
                                onClick={() => {
                                  if (addingNoteMeter === meter.id) {
                                    setAddingNoteMeter(null);
                                    setNewNoteText('');
                                  } else {
                                    setAddingNoteMeter(meter.id);
                                    setAddingNotePlant(null);
                                    setNewNoteText('');
                                  }
                                }}
                              >
                                {meter.meterNotes?.length > 0 ? <WarningAmberIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>

                        {/* Note intervento esistenti per questo contatore */}
                        {meter.meterNotes?.map(note => (
                          <TableRow key={`note-${note.id}`}>
                            <TableCell 
                              colSpan={showInsoluto ? 8 : 7}
                              sx={{ 
                                bgcolor: '#fff3e0', 
                                borderLeft: '4px solid #ff9800',
                                py: 0.5,
                                px: 2,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <WarningAmberIcon sx={{ color: '#e65100', fontSize: 16 }} />
                                  <Typography variant="body2" sx={{ color: '#e65100', fontStyle: 'italic' }}>
                                    <strong>INTERVENTO:</strong> {note.nota}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#999' }}>
                                    ({formatDate(note.data_creazione)})
                                  </Typography>
                                </Box>
                                <IconButton size="small" onClick={() => handleDeleteNote(note.id)} sx={{ color: '#999' }}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* Form inline aggiunta nota contatore */}
                        <TableRow>
                          <TableCell colSpan={showInsoluto ? 8 : 7} sx={{ p: 0, border: 'none' }}>
                            <Collapse in={addingNoteMeter === meter.id}>
                              <Box sx={{ 
                                display: 'flex', 
                                gap: 1, 
                                p: 1.5, 
                                bgcolor: '#fff8e1',
                                borderLeft: '4px solid #ffc107',
                                alignItems: 'center'
                              }}>
                                <BuildIcon sx={{ color: '#f57f17', fontSize: 20 }} />
                                <TextField
                                  size="small"
                                  placeholder="Descrivi l'intervento da fare su questo contatore..."
                                  value={addingNoteMeter === meter.id ? newNoteText : ''}
                                  onChange={(e) => setNewNoteText(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      handleAddMeterNote(meter.id, meter.plant_id);
                                    }
                                    if (e.key === 'Escape') {
                                      setAddingNoteMeter(null);
                                      setNewNoteText('');
                                    }
                                  }}
                                  fullWidth
                                  autoFocus
                                  disabled={savingNote}
                                />
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="warning"
                                  onClick={() => handleAddMeterNote(meter.id, meter.plant_id)}
                                  disabled={savingNote || !newNoteText.trim()}
                                  startIcon={savingNote ? <CircularProgress size={16} /> : <SendIcon />}
                                  sx={{ minWidth: 100 }}
                                >
                                  Salva
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => { setAddingNoteMeter(null); setNewNoteText(''); }}
                                >
                                  Annulla
                                </Button>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Sezione Note Impianto in fondo */}
              <Box sx={{ p: 2 }}>
                {/* Note impianto esistenti */}
                {plant.plantNotes?.length > 0 && (
                  <Box sx={{ 
                    bgcolor: '#fff3e0', 
                    border: '2px solid #ff9800', 
                    borderRadius: 1, 
                    p: 2, 
                    mb: 1 
                  }}>
                    <Typography variant="subtitle2" sx={{ color: '#e65100', fontWeight: 'bold', mb: 1, textTransform: 'uppercase' }}>
                      ⚠️ Note Intervento Impianto
                    </Typography>
                    {plant.plantNotes.map(note => (
                      <Box key={note.id} sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        py: 0.5,
                        borderBottom: '1px dotted #ffcc80',
                        '&:last-child': { borderBottom: 'none' }
                      }}>
                        <Typography variant="body2" sx={{ color: '#bf360c' }}>
                          • {note.nota}
                          <Typography component="span" variant="caption" sx={{ color: '#999', ml: 1 }}>
                            ({formatDate(note.data_creazione)})
                          </Typography>
                        </Typography>
                        <IconButton size="small" onClick={() => handleDeleteNote(note.id)} sx={{ color: '#999' }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Bottone/form aggiunta nota impianto */}
                <Collapse in={addingNotePlant === plant.plant_id}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    p: 1.5, 
                    bgcolor: '#fff8e1',
                    borderLeft: '4px solid #ffc107',
                    borderRadius: 1,
                    alignItems: 'center',
                    mb: 1
                  }}>
                    <BuildIcon sx={{ color: '#f57f17', fontSize: 20 }} />
                    <TextField
                      size="small"
                      placeholder="Descrivi l'intervento da fare su questo impianto..."
                      value={addingNotePlant === plant.plant_id ? newNoteText : ''}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleAddPlantNote(plant.plant_id);
                        }
                        if (e.key === 'Escape') {
                          setAddingNotePlant(null);
                          setNewNoteText('');
                        }
                      }}
                      fullWidth
                      autoFocus
                      disabled={savingNote}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      color="warning"
                      onClick={() => handleAddPlantNote(plant.plant_id)}
                      disabled={savingNote || !newNoteText.trim()}
                      startIcon={savingNote ? <CircularProgress size={16} /> : <SendIcon />}
                      sx={{ minWidth: 100 }}
                    >
                      Salva
                    </Button>
                    <Button
                      size="small"
                      onClick={() => { setAddingNotePlant(null); setNewNoteText(''); }}
                    >
                      Annulla
                    </Button>
                  </Box>
                </Collapse>

                <Button
                  variant="outlined"
                  size="small"
                  color="warning"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    if (addingNotePlant === plant.plant_id) {
                      setAddingNotePlant(null);
                      setNewNoteText('');
                    } else {
                      setAddingNotePlant(plant.plant_id);
                      setAddingNoteMeter(null);
                      setNewNoteText('');
                    }
                  }}
                >
                  Aggiungi nota impianto
                </Button>
              </Box>
            </Paper>
          ))}

          {/* Footer con bottone stampa */}
          <Box sx={{ textAlign: 'center', mt: 3, mb: 2 }}>
            <Button 
              variant="contained" 
              size="large"
              startIcon={isPrinting ? <CircularProgress size={24} color="inherit" /> : <PrintIcon />}
              onClick={handlePrint}
              disabled={isPrinting}
              sx={{ px: 5, py: 1.5 }}
            >
              {isPrinting ? 'Generazione PDF...' : 'Genera e Scarica PDF'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ReadingSheetPreview;
